import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { selectCurrentSpaceId } from "modules/space/redux/selectors";
import { getEmbeddedVideoURL } from "helpers/videoHelper";
import VideoPlayerIFrame from "modules/videoPlayer/VideoPlayerIFrame";
import { useI18n } from "i18n";
import classNames from "classnames";
import { IWelcomeMessage } from "modules/event/types";
import { getUserName } from "modules/userProfile";
import { selectIsMobile } from "modules/deviceInfo";
import { makeStyles } from "@remo-co/ui-core/src/utils/makeStyles";
import { Theme } from "@remo-co/ui-core/src/types";
import { DialogContent } from "@remo-co/ui-core/src/components/DialogContent";
import { Dialog } from "@remo-co/ui-core/src/components/Dialog";
import { Button } from "@remo-co/ui-core/src/components/Button";
import { Paper } from "@remo-co/ui-core/src/components/Paper";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { Box } from "@remo-co/ui-core/src/components/Box";
import { selectUser } from "modules/auth/redux/selectors";
import { selectCurrentUserPreferences } from "modules/userPreferences/redux/selectors";
import { selectIsBroadcasting } from "modules/broadcast/redux/selectors";
import { selectCurrentEvent } from "modules/event/selectors";
import { updateEventUserPreference } from "../../../services/firebaseService/userPreferences.firebase";
import useSteps, { Step } from "../redux/hooks/useSteps";

const useStyles = makeStyles<Theme>((theme) => ({
  img: {
    maxWidth: "100%",
    maxHeight: "32vh",
    minHeight: "unset !important",
  },
  imgMobile: {
    minHeight: "auto",
  },
  dialogContent: {
    padding: 20,
  },
  body1: {
    whiteSpace: "pre-wrap",
  },
  box: {
    margin: "1rem",
  },
  view: {
    "text-align": "center",
    padding: "0 1rem",
    "box-sizing": "border-box",
    "box-shadow": "none",
    "overflow-x": "hidden",

    [theme.breakpoints.down("lg")]: {
      padding: 0,
    },
    "& h6": {
      whiteSpace: "nowrap",
      fontSize: "1.5rem",

      [theme.breakpoints.down("lg")]: {
        whiteSpace: "normal",
        fontSize: "1.25rem",
      },
    },
    "& img": {
      "min-height": "220px",
    },
    "& button": {
      margin: "2rem 0 3px",
    },
  },
}));

const WelcomeMessage = () => {
  const { t } = useI18n(["common", "onboarding"]);
  const STEPS = useSteps();
  const onBoardingSteps = [STEPS[0]];
  const user = useSelector(selectUser);
  const userPreference = useSelector(selectCurrentUserPreferences);
  const isBroadcasting = useSelector(selectIsBroadcasting);
  const currentEvent = useSelector(selectCurrentEvent);
  const spaceId = useSelector(selectCurrentSpaceId);
  const isMobileView = useSelector(selectIsMobile);
  const [allowPlayVideo] = useState<boolean>(true);
  const [isLoading, setLoading] = useState(true);
  const [completed, setCompleted] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [preferencesFullyLoaded, setPreferencesFullyLoaded] = useState(false);
  const userId = user ? user.id : null;
  const theme = useStyles();

  const setShown = () => {
    updateEventUserPreference({ welcomeMessageSeen: true });
    setCompleted(true);
    setModalOpen(false);
  };

  // Add effect to check if userPreference has all required keys
  useEffect(() => {
    if (userPreference && "roomId" in userPreference) {
      setPreferencesFullyLoaded(true);
    }
  }, [userPreference]);

  useEffect(() => {
    if (isBroadcasting) {
      setShown();
    }
  }, [isBroadcasting]);

  useEffect(() => {
    // Only proceed if preferences are fully loaded
    if (!preferencesFullyLoaded || !currentEvent || !spaceId || !userId) {
      return;
    }

    const userHasSeenWelcomeMessage = userPreference?.welcomeMessageSeen;

    if (isBroadcasting && !userHasSeenWelcomeMessage) {
      setShown();
    }

    if (
      currentEvent.status === "inactive" ||
      currentEvent.status === "archived"
    ) {
      setShown();
    }

    if (
      userHasSeenWelcomeMessage ||
      !currentEvent.welcomeMessage ||
      !currentEvent.welcomeMessage.isActive
    ) {
      setLoading(false);
      setModalOpen(false);
      return;
    }

    setModalOpen(true);
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spaceId, userId, userPreference, currentEvent, preferencesFullyLoaded]);

  const renderContent = (content: string) => {
    if (!content) {
      return;
    }

    if (!user) {
      // eslint-disable-next-line consistent-return
      return content;
    }

    // eslint-disable-next-line consistent-return
    return content.replace("##user", getUserName(user));
  };

  const renderMedia = (
    step: Step,
    index: number,
    welcomeMessage: IWelcomeMessage,
  ) => {
    const isValidVideo =
      welcomeMessage.mediaType === "video" &&
      welcomeMessage.mediaURL &&
      welcomeMessage.isActive;
    const isValidImage =
      welcomeMessage.mediaType === "image" &&
      welcomeMessage.mediaURL &&
      welcomeMessage.isActive;
    const embeddedUrl = getEmbeddedVideoURL(welcomeMessage.mediaURL || "");

    if (index === 0 && isValidVideo && allowPlayVideo && embeddedUrl) {
      return (
        <Box
          width="100%"
          display="flex"
          justifyContent="center"
          data-testid="video-box"
        >
          {isMobileView ? (
            <VideoPlayerIFrame src={embeddedUrl} allowFullScreen />
          ) : (
            <Box width={450} height={250} className={theme.box}>
              <VideoPlayerIFrame src={embeddedUrl} allowFullScreen />
            </Box>
          )}
        </Box>
      );
    }

    if (index === 0 && isValidImage) {
      return (
        <figure>
          <img
            className={classNames(
              { [theme.imgMobile]: isMobileView },
              theme.img,
            )}
            src={welcomeMessage.mediaURL}
            alt={`${t(step.title)}`}
            data-testid="image"
          />
        </figure>
      );
    }

    if (index === 0 && welcomeMessage.isActive) {
      return null;
    }

    return <img src={step.image} alt={`${t(step.title)}`} />;
  };

  const getCustomizedSteps = () => {
    const event = currentEvent;
    const welcomeMessage =
      event && event.welcomeMessage
        ? event.welcomeMessage
        : {
            isActive: false,
            title: "",
            message: "",
          };

    return onBoardingSteps.map((step, index) => (
      <Paper className={theme.view} key={`${t(step.title)}` || index}>
        <Typography variant="h6" data-testid="welcome-message-title">
          {index === 0 && welcomeMessage.isActive
            ? welcomeMessage.title
            : `${t(step.title)}`}
        </Typography>
        {renderMedia(step, index, welcomeMessage)}
        <Typography
          variant="body1"
          className={theme.body1}
          data-testid="welcome-message-title"
        >
          {index === 0 && welcomeMessage.isActive
            ? renderContent(welcomeMessage.message ?? "")
            : renderContent(`${t(step.content ?? "")}`)}
        </Typography>
        <Button variant="primary" color="blue" onClick={setShown}>
          {t(step.button ?? "")}
        </Button>
      </Paper>
    ));
  };

  // Add preferencesFullyLoaded check to prevent rendering until fully loaded
  if (isLoading || !preferencesFullyLoaded) {
    return null;
  }

  if (!modalOpen || completed) {
    return null;
  }

  return (
    <Dialog
      scroll="paper"
      fullWidth
      maxWidth="sm"
      aria-labelledby={t("onboarding:label.onboarding.tutorials")}
      open={modalOpen}
      id="onboard-modal"
      data-testid="welcome-message-modal"
    >
      <DialogContent className={theme.dialogContent}>
        {getCustomizedSteps()}
      </DialogContent>
    </Dialog>
  );
};

export default WelcomeMessage;
